import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";

const Shell = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-page px-4">
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-md rounded-xl border border-line bg-white p-10 text-center shadow-sm"
    >
      {children}
    </motion.div>
  </div>
);

const HomeButton = ({ testid, label }) => (
  <Link
    to="/"
    data-testid={testid}
    className="mt-8 inline-block rounded-md bg-brand px-10 py-3 text-sm font-bold text-brandink transition-colors duration-200 hover:bg-branddark"
  >
    {label}
  </Link>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const ref = params.get("ref");
  const [state, setState] = useState("verifying");
  const { t } = useLanguage();
  const tries = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (!sessionId) return setState("pending");
      try {
        const data = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") return setState("confirmed");
      } catch (e) {
        /* keep polling */
      }
      if (!cancelled && ++tries.current < 25) setTimeout(poll, 2000);
      else if (!cancelled) setState("pending");
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <Shell>
      {state === "verifying" && (
        <div data-testid="payment-verifying">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-link" />
          <h1 className="mt-6 text-2xl font-extrabold text-brandink">{t("ps_verifying")}</h1>
          <p className="mt-3 text-sm text-muted">{t("ps_verifying_sub")}</p>
        </div>
      )}
      {state === "confirmed" && (
        <div data-testid="payment-confirmed">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }}>
            <CheckCircle2 className="mx-auto h-14 w-14 text-success" />
          </motion.div>
          <h1 className="mt-6 text-2xl font-extrabold text-brandink">{t("ps_confirmed")}</h1>
          {ref && (
            <p data-testid="payment-order-ref" className="mt-5 inline-block rounded-md border border-brand/60 bg-[#FFFBE0] px-6 py-2.5 font-mono text-lg font-bold tracking-widest text-brandink">
              {ref}
            </p>
          )}
          <p className="mt-5 text-sm leading-relaxed text-muted">{t("ps_confirmed_sub")}</p>
          <HomeButton testid="payment-home-button" label={t("ps_home")} />
        </div>
      )}
      {state === "pending" && (
        <div data-testid="payment-pending">
          <Clock className="mx-auto h-12 w-12 text-link" />
          <h1 className="mt-6 text-2xl font-extrabold text-brandink">{t("ps_pending")}</h1>
          {ref && <p className="mt-4 font-mono font-bold tracking-widest text-brandink">{ref}</p>}
          <p className="mt-4 text-sm leading-relaxed text-muted">{t("ps_pending_sub")}</p>
          <HomeButton testid="payment-home-button" label={t("ps_home")} />
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  const { t } = useLanguage();
  return (
    <Shell>
      <div data-testid="payment-cancelled">
        <XCircle className="mx-auto h-12 w-12 text-muted" />
        <h1 className="mt-6 text-2xl font-extrabold text-brandink">{t("pc_title")}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted">{t("pc_sub")}</p>
        <HomeButton testid="payment-back-button" label={t("pc_back")} />
      </div>
    </Shell>
  );
}
